import { useState, useEffect } from 'react';
import { api } from '../lib/api.js';
import { ScheduleGantt } from './ScheduleGantt.jsx';
import { kioskApi } from '../lib/kiosk-api.js';
import { todayStr } from '../lib/format.js';

// ─── Shift dashboard (today's staff + station assignments) ──────────────────
// Pulls /api/shift-dashboard/{key} — combines Square scheduled shifts with
// the per-shift station assignments stored in shift_station_assignments.
// Manager+: dropdown to set the station per row. Staff/kiosk: read-only.
export function ShiftDashboardView({ location, user, readOnly = false, kioskToken = null }) {
  // On the kiosk, authenticate as the kiosk session. x-user-id alone is the
  // retired legacy header, refused on Cloud Run, which left the kiosk's Today
  // screen showing a red "Not authenticated" box (CJ at Broadway, 2026-10-14).
  const headers = kioskToken
    ? { Authorization: `Kiosk-Token ${kioskToken}` }
    : { 'x-user-id': String(user.id) };
  // Two ways to look at today's shifts: 'list' (the card list with station
  // pickers) or 'gantt' (timeline bars 5am→9pm, color-coded by clock
  // status). Both render from the same data; toggle is local to this
  // component so each user can pick what works for them.
  const [viewMode, setViewMode] = useState(() => {
    try { return localStorage.getItem('monarchShiftView') || 'list'; } catch { return 'list'; }
  });
  function selectView(mode) {
    setViewMode(mode);
    try { localStorage.setItem('monarchShiftView', mode); } catch {}
  }
  // Three editing modes:
  //   - kioskToken set → any kiosk user can assign stations (writes via the
  //     kiosk endpoint; auth via Kiosk-Token).
  //   - regular site, manager+ → can assign stations (writes via the
  //     manager endpoint; auth via x-user-id).
  //   - regular site, non-manager OR readOnly prop → read-only.
  const canEditStations = !readOnly && (kioskToken || window.EmpireRoles.isManager(user));
  const locationKey = String(location.name || '').toLowerCase().replace(/\s+/g, '_');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [date, setDate] = useState(() => todayStr());

  async function load() {
    setLoading(true); setErr('');
    try {
      const d = await api('GET', `/api/shift-dashboard/${locationKey}?date=${date}`, null, headers);
      setData(d);
    } catch (e) { setErr(e.message || String(e)); }
    finally { setLoading(false); }
  }
  useEffect(() => { load(); }, [locationKey, date]);

  async function setStation(shift, stationId) {
    try {
      if (kioskToken) {
        await kioskApi('PUT', `/api/kiosk/${locationKey}/shift-station`, {
          square_shift_id: shift.assignmentKey,
          ymd: date,
          station_id: stationId,
        }, kioskToken);
      } else {
        await api('PUT', '/api/shift-station', {
          square_shift_id: shift.assignmentKey,
          ymd: date,
          location_id: data?.meta?.locationId,
          station_id: stationId,
        }, headers);
      }
      // Optimistic update
      setData(d => d && {
        ...d,
        shifts: d.shifts.map(s => s.assignmentKey === shift.assignmentKey ? { ...s, stationId } : s),
      });
    } catch (e) { setErr(e.message || String(e)); }
  }

  if (loading) return <div className="card text-sm text-gray-500">Loading shift dashboard…</div>;
  if (err) return <div className="card" style={{ borderLeft: '4px solid #c62828' }}><div className="text-sm font-semibold text-red-700">Couldn't load</div><div className="text-xs text-gray-600 mt-1">{err}</div></div>;
  if (!data) return null;

  const stationsById = Object.fromEntries((data.stations || []).map(s => [s.id, s]));

  return (
    <div className="space-y-4">
      <div className="card" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <div>
          <div style={{ fontSize: 14, fontWeight: 700 }}>{data.meta.locationName} · Shift dashboard</div>
          <div style={{ fontSize: 11, color: '#9e9e9e' }}>
            {data.staffOnClock} on clock · {data.staffScheduled} scheduled · {data.shifts.length} shifts today
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          {/* List ↔ Gantt toggle. Persisted in localStorage so the same
              choice sticks across reloads on the same device. */}
          <div style={{ display: 'inline-flex', border: '1px solid #d0d4d9', borderRadius: 6, overflow: 'hidden' }}>
            {[['list', 'List'], ['gantt', 'Gantt']].map(([id, label]) => (
              <button key={id} onClick={() => selectView(id)}
                style={{ padding: '5px 12px', fontSize: 12, fontWeight: 600,
                          background: viewMode === id ? 'var(--forest)' : '#fff',
                          color: viewMode === id ? '#fff' : '#37474f',
                          border: 'none', cursor: 'pointer', fontFamily: 'inherit' }}>
                {label}
              </button>
            ))}
          </div>
          {!readOnly && (
            <input type="date" value={date} onChange={e => setDate(e.target.value)}
              style={{ padding: '5px 8px', fontSize: 13, border: '1px solid #d0d4d9', borderRadius: 6 }} />
          )}
        </div>
      </div>

      {data.clockAlerts && data.clockAlerts.length > 0 && (
        <div className="card" style={{ borderLeft: '4px solid #d97706' }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: '#92400e', marginBottom: 4 }}>Attention</div>
          {data.clockAlerts.map((a, i) => (
            <div key={i} style={{ fontSize: 12, color: '#78350f' }}>{a.message}</div>
          ))}
        </div>
      )}

      {viewMode === 'gantt' ? (
        // Gantt timeline — same component the regular site's Daily report
        // renders. Reuses the existing schedule.shifts shape returned by
        // /api/shift-dashboard, which is structurally identical to
        // /api/schedule (employeeName, scheduledStart, scheduledEnd,
        // clockStatus, lateMinutes, etc).
        <ScheduleGantt schedule={{ shifts: data.shifts, staffOnClock: data.staffOnClock, staffScheduled: data.staffScheduled }}
          isToday={date === (todayStr())}
          statusBadge={date === (todayStr()) ? 'live' : 'past'}
          loading={false} error={null} />
      ) : (
        <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
          {data.shifts.length === 0 ? (
            <div style={{ padding: 16, fontSize: 13, color: '#9e9e9e', textAlign: 'center' }}>
              No shifts scheduled for {date}.
            </div>
          ) : (
            data.shifts.map((s, i) => {
              const st = s.stationId ? stationsById[s.stationId] : null;
              return (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px', borderBottom: '1px solid #f5f5f5' }}>
                  <div style={{ width: 36, height: 36, borderRadius: '50%', background: '#e8f5e9', color: 'var(--forest)',
                                fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    {(s.employeeName || '?').slice(0, 1).toUpperCase()}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: '#212121' }}>{s.employeeName || '(unknown)'}</div>
                    <div style={{ fontSize: 11, color: '#757575' }}>
                      {s.scheduledStart}{s.scheduledEnd ? `–${s.scheduledEnd}` : ''}
                      {s.jobTitle ? ` · ${s.jobTitle}` : ''}
                      {s.clockStatus ? ` · ${s.clockStatus.replace(/_/g, ' ')}` : ''}
                    </div>
                  </div>
                  <div style={{ flexShrink: 0 }}>
                    {canEditStations ? (
                      <select value={s.stationId || ''} onChange={e => setStation(s, e.target.value ? Number(e.target.value) : null)}
                        style={{ padding: '5px 8px', fontSize: 12, border: '1px solid #d0d4d9', borderRadius: 4 }}>
                        <option value="">— Station —</option>
                        {(data.stations || []).map(opt => (
                          <option key={opt.id} value={opt.id}>{opt.name}</option>
                        ))}
                      </select>
                    ) : (
                      <span style={{ fontSize: 12, fontWeight: 600,
                                      color: st ? 'var(--forest)' : '#9e9e9e',
                                      padding: '4px 10px', borderRadius: 12,
                                      background: st ? '#e8f5e9' : '#f5f5f5' }}>
                        {st ? st.name : 'Unassigned'}
                      </span>
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>
      )}

      {(data.stations || []).length === 0 && canEditStations && (
        <div className="card" style={{ background: '#fffbeb', borderLeft: '4px solid #d97706' }}>
          <div style={{ fontSize: 12, color: '#78350f' }}>
            No stations configured for this location yet. Go to <strong>Settings → Locations → Stations</strong> to add them.
          </div>
        </div>
      )}
    </div>
  );
}
